/**
 * MasterAccess — v109.9.52
 *
 * Master-admin-only page inside AdminHub. Grants module-level (and tab-level)
 * View / Edit / Delete permissions to individual users, via a reusable Role
 * system + per-user overrides. Also lets the admin mark modules as "Common"
 * (auto view-only for every logged-in user), and view an Access Matrix in
 * both directions (User×Module and Module×User).
 *
 * All heavy lifting happens on the backend (routers/access.py). This UI is
 * a thin editor + reporter.
 */
import { useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api, { formatError } from "../lib/api";
import { toast } from "sonner";
import {
  ShieldCheck, Users, KeyRound, Grid3x3, Globe2, Search,
  Save, Trash2, Plus, Edit3, ChevronDown, ChevronRight,
  Eye, Pencil, X, Check, ArrowLeftRight,
} from "lucide-react";
import { PageHeader } from "../components/GradientCard";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import { Badge } from "../components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";

// ─────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────
const ACTIONS = [
  { key: "view",   label: "View",   icon: Eye,    tone: "sky"     },
  { key: "edit",   label: "Edit",   icon: Pencil, tone: "amber"   },
  { key: "delete", label: "Delete", icon: Trash2, tone: "rose"    },
];

const emptyPerm = () => ({ view: false, edit: false, delete: false });

// Union merge two permission dicts.
const mergePerm = (a, b) => ({
  view:   !!(a?.view   || b?.view),
  edit:   !!(a?.edit   || b?.edit),
  delete: !!(a?.delete || b?.delete),
});

// ─────────────────────────────────────────────────────────────────────────
// Sub-component: Module permission editor (used in Role & Grant editors).
// ─────────────────────────────────────────────────────────────────────────
function ModulePermEditor({ catalog, permissions, onChange, disabledReason }) {
  const [expanded, setExpanded] = useState({});
  const filterGroups = useMemo(() => {
    const m = new Map();
    catalog.forEach((mod) => {
      const g = mod.group || "Other";
      if (!m.has(g)) m.set(g, []);
      m.get(g).push(mod);
    });
    return Array.from(m.entries()).map(([group, mods]) => ({ group, mods }));
  }, [catalog]);

  const setModulePerm = (mk, action, value) => {
    const next = { ...(permissions || {}) };
    next[mk] = { ...(next[mk] || emptyPerm()), [action]: value };
    // If we unchecked view, the tab-level view rights don't matter — leave tabs as-is.
    onChange(next);
  };

  const setTabPerm = (mk, tk, action, value) => {
    const next = { ...(permissions || {}) };
    const mod = { ...(next[mk] || emptyPerm()) };
    const tabs = { ...(mod.tabs || {}) };
    tabs[tk] = { ...(tabs[tk] || emptyPerm()), [action]: value };
    mod.tabs = tabs;
    next[mk] = mod;
    onChange(next);
  };

  const setSubtabPerm = (mk, tk, stk, action, value) => {
    const next = { ...(permissions || {}) };
    const mod = { ...(next[mk] || emptyPerm()) };
    const tabs = { ...(mod.tabs || {}) };
    const tab = { ...(tabs[tk] || emptyPerm()) };
    const subs = { ...(tab.subtabs || {}) };
    subs[stk] = { ...(subs[stk] || emptyPerm()), [action]: value };
    tab.subtabs = subs;
    tabs[tk] = tab;
    mod.tabs = tabs;
    next[mk] = mod;
    onChange(next);
  };

  const toggleAllForModule = (mk, mod) => {
    const next = { ...(permissions || {}) };
    const current = next[mk] || emptyPerm();
    const all = current.view && current.edit && current.delete;
    const flip = !all;
    next[mk] = { view: flip, edit: flip, delete: flip };
    if (mod?.tabs?.length) {
      const tabs = {};
      mod.tabs.forEach((t) => {
        tabs[t.key] = { view: flip, edit: flip, delete: flip };
      });
      next[mk].tabs = tabs;
    }
    onChange(next);
  };

  return (
    <div className="space-y-4">
      {disabledReason && (
        <div className="text-[11px] text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/30 rounded-md px-3 py-2">
          {disabledReason}
        </div>
      )}
      {filterGroups.map(({ group, mods }) => (
        <div key={group} className="border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden">
          <div className="px-3 py-2 bg-slate-50/70 dark:bg-slate-800/50 text-[11px] uppercase tracking-wider font-bold text-slate-600 dark:text-slate-300 border-b border-slate-200 dark:border-slate-700">
            {group}
          </div>
          <div className="divide-y divide-slate-100 dark:divide-slate-800">
            {mods.map((mod) => {
              const p = permissions?.[mod.key] || emptyPerm();
              const isOpen = expanded[mod.key];
              const hasTabs = (mod.tabs || []).length > 0;
              const anyPerm = p.view || p.edit || p.delete;
              return (
                <div key={mod.key}>
                  <div className={`px-3 py-2 flex items-center gap-2 ${anyPerm ? "bg-indigo-50/30 dark:bg-indigo-500/8" : ""}`}>
                    {hasTabs ? (
                      <button
                        type="button"
                        onClick={() => setExpanded({ ...expanded, [mod.key]: !isOpen })}
                        className="w-5 h-5 grid place-items-center text-slate-500 hover:text-indigo-600 dark:text-slate-400 dark:hover:text-indigo-300"
                        data-testid={`ma-tabs-toggle-${mod.key}`}
                      >
                        {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                      </button>
                    ) : (
                      <span className="w-5" />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="text-[12.5px] font-bold text-slate-900 dark:text-white truncate">{mod.label}</div>
                      <div className="text-[10px] text-slate-500 dark:text-slate-400 mono truncate">{mod.key}</div>
                    </div>
                    <div className="flex items-center gap-1.5">
                      {ACTIONS.map(({ key, label, icon: Icon }) => (
                        <label
                          key={key}
                          className={`inline-flex items-center gap-1 px-2 py-1 text-[10.5px] rounded-md border cursor-pointer select-none transition-colors ${
                            p[key]
                              ? "bg-indigo-600 text-white border-indigo-700 dark:bg-indigo-500 dark:border-indigo-400"
                              : "bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:border-indigo-300"
                          }`}
                          data-testid={`ma-perm-${mod.key}-${key}`}
                        >
                          <input
                            type="checkbox"
                            checked={!!p[key]}
                            onChange={(e) => setModulePerm(mod.key, key, e.target.checked)}
                            className="hidden"
                          />
                          <Icon size={11} /> {label}
                        </label>
                      ))}
                      <button
                        type="button"
                        onClick={() => toggleAllForModule(mod.key, mod)}
                        className="text-[10px] px-2 py-1 rounded-md border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 font-semibold"
                        data-testid={`ma-perm-all-${mod.key}`}
                      >
                        All
                      </button>
                    </div>
                  </div>
                  {hasTabs && isOpen && (
                    <div className="pl-10 pr-3 py-2 bg-slate-50/50 dark:bg-slate-800/30 space-y-1.5">
                      {mod.tabs.map((t) => {
                        const tp = p.tabs?.[t.key] || emptyPerm();
                        const hasSubs = (t.subtabs || []).length > 0;
                        const tabExpandKey = `${mod.key}::${t.key}`;
                        const tabOpen = !!expanded[tabExpandKey];
                        return (
                          <div key={t.key}>
                            <div className="flex items-center gap-2">
                              {hasSubs ? (
                                <button
                                  type="button"
                                  onClick={() => setExpanded({ ...expanded, [tabExpandKey]: !tabOpen })}
                                  className="w-4 h-4 grid place-items-center text-slate-500 hover:text-indigo-600 dark:text-slate-400 dark:hover:text-indigo-300"
                                  data-testid={`ma-subtabs-toggle-${mod.key}-${t.key}`}
                                >
                                  {tabOpen ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                                </button>
                              ) : (
                                <span className="w-4" />
                              )}
                              <div className="flex-1 min-w-0 text-[11.5px] text-slate-700 dark:text-slate-200 truncate">
                                <span className="font-medium">{t.label}</span>
                                <span className="ml-1.5 mono text-[10px] text-slate-500 dark:text-slate-400">{t.key}</span>
                                {hasSubs && <span className="ml-1 text-[9px] text-slate-400">· {t.subtabs.length} sub-tabs</span>}
                              </div>
                              {ACTIONS.map(({ key, label, icon: Icon }) => (
                                <label
                                  key={key}
                                  className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] rounded border cursor-pointer transition-colors ${
                                    tp[key]
                                      ? "bg-indigo-500 text-white border-indigo-600"
                                      : "bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:border-indigo-300"
                                  }`}
                                  data-testid={`ma-perm-tab-${mod.key}-${t.key}-${key}`}
                                >
                                  <input
                                    type="checkbox"
                                    checked={!!tp[key]}
                                    onChange={(e) => setTabPerm(mod.key, t.key, key, e.target.checked)}
                                    className="hidden"
                                  />
                                  <Icon size={10} /> {label}
                                </label>
                              ))}
                            </div>
                            {hasSubs && tabOpen && (
                              <div className="pl-8 mt-1 space-y-1">
                                {t.subtabs.map((st) => {
                                  const sp = tp.subtabs?.[st.key] || emptyPerm();
                                  return (
                                    <div key={st.key} className="flex items-center gap-2">
                                      <span className="w-3 h-3 rounded-full bg-slate-300 dark:bg-slate-600 inline-block" />
                                      <div className="flex-1 min-w-0 text-[11px] text-slate-600 dark:text-slate-300 truncate">
                                        <span className="font-medium">{st.label}</span>
                                        <span className="ml-1.5 mono text-[9.5px] text-slate-400 dark:text-slate-500">{st.key}</span>
                                      </div>
                                      {ACTIONS.map(({ key, label, icon: Icon }) => (
                                        <label
                                          key={key}
                                          className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-[9.5px] rounded border cursor-pointer transition-colors ${
                                            sp[key]
                                              ? "bg-indigo-500 text-white border-indigo-600"
                                              : "bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-500 dark:text-slate-300 hover:border-indigo-300"
                                          }`}
                                          data-testid={`ma-perm-subtab-${mod.key}-${t.key}-${st.key}-${key}`}
                                        >
                                          <input
                                            type="checkbox"
                                            checked={!!sp[key]}
                                            onChange={(e) => setSubtabPerm(mod.key, t.key, st.key, key, e.target.checked)}
                                            className="hidden"
                                          />
                                          <Icon size={9} /> {label}
                                        </label>
                                      ))}
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Main page
// ─────────────────────────────────────────────────────────────────────────
export default function MasterAccess({ embedded = false }) {
  const { user } = useAuth();
  const [catalog, setCatalog] = useState([]);
  const [roles, setRoles] = useState([]);
  const [commonScopes, setCommonScopes] = useState([]);
  const [fixedScopes, setFixedScopes] = useState([]);
  const [users, setUsers] = useState([]);
  const [matrix, setMatrix] = useState({ users: [], modules: {} });
  const [loading, setLoading] = useState(false);

  // Role editor state
  const [roleDialog, setRoleDialog] = useState(null); // { id?, name, description, permissions }
  const [savingRole, setSavingRole] = useState(false);

  // Grant editor state
  const [selectedUserId, setSelectedUserId] = useState("");
  const [selectedGrant, setSelectedGrant] = useState({ role_ids: [], overrides: {} });
  const [savingGrant, setSavingGrant] = useState(false);

  // Matrix view state
  const [matrixView, setMatrixView] = useState("user"); // "user" | "module"
  const [matrixSearch, setMatrixSearch] = useState("");

  // v109.9.62 — Grants cache for showing "N users assigned" on each role card
  // and driving the RoleMembersDialog.
  const [grantsCache, setGrantsCache] = useState([]);
  const [roleMembersDialog, setRoleMembersDialog] = useState(null); // { role, userIds }
  // v109.9.63 — Legacy role → default modules reference
  const [legacyRoles, setLegacyRoles] = useState([]);
  const [legacyAllModules, setLegacyAllModules] = useState([]);
  // v109.9.64 — Editable legacy role dialog
  const [legacyEdit, setLegacyEdit] = useState(null); // {key, label, modules, initial: [...]}

  // Load all bootstrap data
  const loadAll = async () => {
    setLoading(true);
    try {
      const [c, r, cm, u, m, g, lr] = await Promise.all([
        api.get("/access/catalog"),
        api.get("/access/roles"),
        api.get("/access/common"),
        api.get("/employees"),
        api.get("/access/matrix"),
        api.get("/access/grants"),
        api.get("/access/roles/legacy"),
      ]);
      setCatalog(c.data?.modules || []);
      setRoles(r.data || []);
      setCommonScopes(cm.data?.scopes || cm.data?.modules || []);
      setFixedScopes(cm.data?.fixed_scopes || []);
      setUsers((u.data || []).filter((x) => x.role !== "master_admin"));
      setMatrix(m.data || { users: [], modules: {} });
      setGrantsCache(g.data || []);
      setLegacyRoles(lr.data?.roles || []);
      setLegacyAllModules(lr.data?.all_module_keys || []);
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
    } finally { setLoading(false); }
  };

  useEffect(() => { loadAll(); }, []);

  // v109.9.62 — Role membership dialog helpers
  const openRoleMembers = (role) => {
    const uids = (grantsCache || [])
      .filter((g) => (g.role_ids || []).includes(role.id) || g.role_id === role.id)
      .map((g) => g.user_id);
    setRoleMembersDialog({ role, userIds: uids });
  };
  const saveRoleMembers = async (userIds) => {
    if (!roleMembersDialog) return;
    try {
      await api.put(`/access/roles/${roleMembersDialog.role.id}/users`, { user_ids: userIds });
      toast.success(`Role membership saved (${userIds.length} user${userIds.length === 1 ? "" : "s"})`);
      setRoleMembersDialog(null);
      await loadAll();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  // v109.9.64 — Legacy role editing (module list per legacy role, overrides ROLE_MODULES)
  const openLegacyEdit = (lr) => {
    setLegacyEdit({ key: lr.key, label: lr.label, modules: [...(lr.modules || [])], initial: [...(lr.modules || [])] });
  };
  const saveLegacyEdit = async () => {
    if (!legacyEdit) return;
    try {
      await api.put(`/access/roles/legacy/${legacyEdit.key}`, { modules: legacyEdit.modules });
      toast.success(`${legacyEdit.label} updated (${legacyEdit.modules.length} module${legacyEdit.modules.length === 1 ? "" : "s"})`);
      setLegacyEdit(null);
      await loadAll();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };
  const resetLegacyEdit = async () => {
    if (!legacyEdit) return;
    try {
      await api.delete(`/access/roles/legacy/${legacyEdit.key}`);
      toast.success(`${legacyEdit.label} reset to ERP default`);
      setLegacyEdit(null);
      await loadAll();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  // ── Roles: create/update/delete
  const openNewRole = () => setRoleDialog({ name: "", description: "", permissions: {} });
  const openEditRole = (r) => setRoleDialog({ id: r.id, name: r.name, description: r.description || "", permissions: r.permissions || {} });
  const saveRole = async () => {
    const payload = { name: roleDialog.name?.trim(), description: roleDialog.description?.trim(), permissions: roleDialog.permissions || {} };
    if (!payload.name) return toast.error("Role name is required");
    setSavingRole(true);
    try {
      if (roleDialog.id) {
        await api.put(`/access/roles/${roleDialog.id}`, payload);
        toast.success("Role updated");
      } else {
        await api.post("/access/roles", payload);
        toast.success("Role created");
      }
      setRoleDialog(null);
      await loadAll();
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
    } finally { setSavingRole(false); }
  };
  const deleteRole = async (id) => {
    if (!window.confirm("Delete this role? Users assigned to it will keep their per-user overrides.")) return;
    try {
      await api.delete(`/access/roles/${id}`);
      toast.success("Role deleted");
      await loadAll();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };
  // v109.9.70 — Delete a legacy role card = reset to hard-coded default modules.
  // Legacy role KEYS live in the codebase (e.g. `hr_admin`) so we can't truly
  // remove them — this drops the DB override and returns the role to its ERP
  // baseline. Users assigned to it stay put; only the module list is wiped.
  const deleteLegacyRole = async (lr) => {
    if (!window.confirm(`Reset "${lr.label}" to the ERP default module list? This clears any custom modules set here.`)) return;
    try {
      await api.delete(`/access/roles/legacy/${lr.key}`);
      toast.success(`${lr.label} reset to ERP default`);
      await loadAll();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  // ── Common access
  const isFixedCommon = (scope) => (fixedScopes || []).includes(scope);
  const toggleCommon = async (scope) => {
    if (isFixedCommon(scope)) {
      toast.error("This scope is a fixed common — it can't be removed.");
      return;
    }
    const next = commonScopes.includes(scope) ? commonScopes.filter((x) => x !== scope) : [...commonScopes, scope];
    setCommonScopes(next);
    try { await api.put("/access/common", { scopes: next }); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); loadAll(); }
  };

  // ── Grants: when a user is picked, load their grant
  const pickUser = async (uid) => {
    setSelectedUserId(uid);
    if (!uid) { setSelectedGrant({ role_ids: [], overrides: {} }); return; }
    try {
      const { data } = await api.get(`/access/grants/${uid}`);
      const rids = Array.isArray(data?.role_ids) && data.role_ids.length
        ? data.role_ids
        : (data?.role_id ? [data.role_id] : []);
      setSelectedGrant({ role_ids: rids, overrides: data?.overrides || {} });
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };
  const saveGrant = async () => {
    if (!selectedUserId) return;
    setSavingGrant(true);
    try {
      await api.put(`/access/grants/${selectedUserId}`, {
        role_ids: selectedGrant.role_ids || [],
        overrides: selectedGrant.overrides || {},
      });
      toast.success("Grant saved");
      await loadAll();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setSavingGrant(false); }
  };
  const clearGrant = async () => {
    if (!selectedUserId) return;
    if (!window.confirm("Clear all direct grants for this user? Common-access modules remain visible.")) return;
    try {
      await api.delete(`/access/grants/${selectedUserId}`);
      setSelectedGrant({ role_ids: [], overrides: {} });
      toast.success("Grant cleared");
      await loadAll();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  if (user?.role !== "master_admin") return <Navigate to="/" replace />;

  const selectedUser = users.find((u) => u.id === selectedUserId);
  const matrixFilteredUsers = matrixSearchFilter(matrix.users, matrixSearch);

  return (
    <div className={embedded ? "space-y-4" : "max-w-[1500px] space-y-6"} data-testid="master-access-page">
      {!embedded && (
        <PageHeader
          tone="violet"
          icon={ShieldCheck}
          eyebrow="Master Admin"
          title="Master Access"
          subtitle={`Grant View / Edit / Delete access at Module + Tab + Sub-Tab level. Roles cascade to users; per-user overrides refine. Common modules are view-only for everyone.`}
        />
      )}

      <Tabs defaultValue="grants" className="w-full">
        <TabsList className="flex flex-wrap h-auto" data-testid="master-access-tabs">
          <TabsTrigger value="grants" data-testid="ma-tab-grants"><Users size={14} className="mr-1.5" /> Grants ({users.length})</TabsTrigger>
          <TabsTrigger value="roles" data-testid="ma-tab-roles"><KeyRound size={14} className="mr-1.5" /> Roles ({roles.length})</TabsTrigger>
          <TabsTrigger value="common" data-testid="ma-tab-common"><Globe2 size={14} className="mr-1.5" /> Common ({commonScopes.length})</TabsTrigger>
          <TabsTrigger value="modules" data-testid="ma-tab-modules"><Grid3x3 size={14} className="mr-1.5" /> All Modules &amp; Tabs</TabsTrigger>
          <TabsTrigger value="matrix" data-testid="ma-tab-matrix"><Grid3x3 size={14} className="mr-1.5" /> Matrix</TabsTrigger>
        </TabsList>

        {/* ═════════════ GRANTS ═════════════ */}
        <TabsContent value="grants" className="mt-4">
          <div className="grid grid-cols-1 lg:grid-cols-[280px,1fr] gap-4">
            {/* User picker */}
            <div className="pk-card p-3" data-testid="ma-user-list">
              <div className="text-[11px] uppercase tracking-wider font-bold text-slate-600 dark:text-slate-300 mb-2">Employees</div>
              <div className="max-h-[560px] overflow-y-auto space-y-1">
                {users.map((u) => (
                  <button
                    key={u.id}
                    onClick={() => pickUser(u.id)}
                    className={`w-full text-left px-2.5 py-2 rounded-md text-[12px] font-medium transition-colors ${
                      selectedUserId === u.id
                        ? "bg-violet-100 dark:bg-violet-500/20 text-violet-800 dark:text-violet-200 ring-1 ring-violet-300 dark:ring-violet-500/40"
                        : "hover:bg-slate-50 dark:hover:bg-slate-800/50 text-slate-700 dark:text-slate-200"
                    }`}
                    data-testid={`ma-user-${u.id}`}
                  >
                    <div className="font-bold truncate">{u.name}</div>
                    <div className="text-[10px] text-slate-500 dark:text-slate-400 truncate">{u.email}</div>
                    <div className="text-[9.5px] uppercase tracking-wider text-slate-400 dark:text-slate-500 mt-0.5">{u.role}</div>
                  </button>
                ))}
                {users.length === 0 && <div className="text-center text-[11px] text-slate-500 py-4">No employees</div>}
              </div>
            </div>

            {/* Grant editor */}
            <div className="pk-card p-4" data-testid="ma-grant-editor">
              {!selectedUser ? (
                <div className="text-center text-[13px] text-slate-500 dark:text-slate-400 py-16">
                  <Users size={22} className="mx-auto mb-2 text-slate-300 dark:text-slate-600" />
                  Select an employee on the left to edit their access.
                </div>
              ) : (
                <>
                  <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
                    <div>
                      <div className="text-[15px] font-extrabold text-slate-900 dark:text-white">{selectedUser.name}</div>
                      <div className="text-[11px] text-slate-500 dark:text-slate-400 mono">{selectedUser.email}</div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button variant="outline" size="sm" onClick={clearGrant} disabled={savingGrant} data-testid="ma-grant-clear">
                        <Trash2 size={13} className="mr-1.5" /> Clear
                      </Button>
                      <Button size="sm" onClick={saveGrant} disabled={savingGrant} data-testid="ma-grant-save">
                        <Save size={13} className="mr-1.5" /> Save
                      </Button>
                    </div>
                  </div>
                  <div className="mb-4">
                    <label className="text-[10.5px] uppercase tracking-wider font-bold text-slate-600 dark:text-slate-300 mb-1 block">Roles (baseline)</label>
                    <div className="rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-2 max-h-40 overflow-y-auto" data-testid="ma-grant-roles">
                      {roles.length === 0 && (
                        <div className="text-center text-[11px] text-slate-400 py-4">No roles defined yet. Create one in the Roles tab.</div>
                      )}
                      {roles.map((r) => {
                        const picked = (selectedGrant.role_ids || []).includes(r.id);
                        return (
                          <label
                            key={r.id}
                            className={`flex items-center gap-2 px-2 py-1 rounded-md cursor-pointer text-[12px] ${picked ? "bg-indigo-50 dark:bg-indigo-500/10" : "hover:bg-slate-50 dark:hover:bg-slate-800/60"}`}
                            data-testid={`ma-grant-role-${r.id}`}
                          >
                            <input
                              type="checkbox"
                              checked={picked}
                              onChange={(e) => {
                                const cur = new Set(selectedGrant.role_ids || []);
                                if (e.target.checked) cur.add(r.id); else cur.delete(r.id);
                                setSelectedGrant({ ...selectedGrant, role_ids: Array.from(cur) });
                              }}
                              className="w-3.5 h-3.5 accent-indigo-600"
                            />
                            <span className="flex-1 font-semibold text-slate-800 dark:text-slate-100 truncate">{r.name}</span>
                            {r.description && <span className="text-[10px] text-slate-500 dark:text-slate-400 truncate">{r.description}</span>}
                          </label>
                        );
                      })}
                    </div>
                    <div className="text-[10.5px] text-slate-500 dark:text-slate-400 mt-1">
                      Pick <b>any number</b> of roles. Every ticked role&apos;s permissions are unioned first, then your overrides below stack on top (any True wins).
                    </div>
                  </div>

                  <div className="text-[10.5px] uppercase tracking-wider font-bold text-slate-600 dark:text-slate-300 mb-2">Overrides</div>
                  <ModulePermEditor
                    catalog={catalog}
                    permissions={selectedGrant.overrides}
                    onChange={(next) => setSelectedGrant({ ...selectedGrant, overrides: next })}
                    disabledReason={null}
                  />
                </>
              )}
            </div>
          </div>
        </TabsContent>

        {/* ═════════════ ROLES ═════════════ */}
        <TabsContent value="roles" className="mt-4">
          <div className="pk-card p-4" data-testid="ma-roles">
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="text-[12px] font-bold text-slate-700 dark:text-slate-200">Reusable role templates</div>
                <div className="text-[10.5px] text-slate-500 dark:text-slate-400 mt-0.5">Custom roles + legacy roles — assign via Grants or the quick-grant buttons on any module page.</div>
              </div>
              <Button size="sm" onClick={openNewRole} data-testid="ma-role-new">
                <Plus size={13} className="mr-1.5" /> New Role
              </Button>
            </div>
            {(roles.length === 0 && legacyRoles.length === 0) ? (
              <div className="text-center text-[12px] text-slate-500 dark:text-slate-400 py-10">
                No roles yet. Create your first template to save time on grants.
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                {/* Custom roles first */}
                {roles.map((r) => {
                  const modCount = Object.keys(r.permissions || {}).length;
                  const assignedCount = (grantsCache || []).filter((g) =>
                    (g.role_ids || []).includes(r.id) || g.role_id === r.id
                  ).length;
                  return (
                    <div key={r.id} className="pk-card p-3" data-testid={`ma-role-item-${r.id}`}>
                      <div className="flex items-start justify-between gap-2 mb-1.5">
                        <div className="min-w-0">
                          <div className="text-[13.5px] font-bold text-slate-900 dark:text-white truncate flex items-center gap-1.5">
                            <span className="truncate">{r.name}</span>
                            <span className="inline-flex items-center text-[9px] font-extrabold px-1.5 py-0.5 rounded-full bg-indigo-600 text-white uppercase tracking-wide" data-keep-color>custom</span>
                          </div>
                          <div className="text-[10.5px] text-slate-500 dark:text-slate-400 line-clamp-2">{r.description || "—"}</div>
                        </div>
                        <div className="flex items-center gap-1">
                          <button onClick={() => openEditRole(r)} className="w-7 h-7 grid place-items-center rounded-md hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-600 dark:text-slate-300" data-testid={`ma-role-edit-${r.id}`} title="Edit permissions">
                            <Edit3 size={13} />
                          </button>
                          <button onClick={() => deleteRole(r.id)} className="w-7 h-7 grid place-items-center rounded-md hover:bg-rose-50 dark:hover:bg-rose-500/15 text-rose-600 dark:text-rose-300" data-testid={`ma-role-del-${r.id}`} title="Delete role">
                            <Trash2 size={13} />
                          </button>
                        </div>
                      </div>
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <Badge className="bg-indigo-100 text-indigo-700 dark:bg-indigo-500/20 dark:text-indigo-300 border-indigo-200 dark:border-indigo-500/40 text-[10px]">
                          {modCount} module{modCount === 1 ? "" : "s"}
                        </Badge>
                        <button
                          onClick={() => openRoleMembers(r)}
                          className="inline-flex items-center gap-1 text-[10.5px] font-bold px-2 py-0.5 rounded-full bg-emerald-50 dark:bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-500/40 hover:bg-emerald-100 dark:hover:bg-emerald-500/25"
                          data-testid={`ma-role-members-${r.id}`}
                        >
                          <Users size={11} /> {assignedCount} user{assignedCount === 1 ? "" : "s"} · Manage
                        </button>
                      </div>
                    </div>
                  );
                })}

                {/* Legacy roles — same card shape as custom roles */}
                {legacyRoles.map((lr) => {
                  const modCount = (lr.modules || []).length;
                  return (
                    <div key={`legacy-${lr.key}`} className="pk-card p-3 ring-1 ring-amber-100 dark:ring-amber-500/20" data-testid={`ma-legacy-role-${lr.key}`}>
                      <div className="flex items-start justify-between gap-2 mb-1.5">
                        <div className="min-w-0">
                          <div className="text-[13.5px] font-bold text-slate-900 dark:text-white truncate flex items-center gap-1.5">
                            <span className="truncate">{lr.label}</span>
                            <span className="inline-flex items-center text-[9px] font-extrabold px-1.5 py-0.5 rounded-full bg-amber-500 text-white uppercase tracking-wide" data-keep-color>legacy</span>
                            {lr.overridden && (
                              <span className="inline-flex items-center text-[9px] font-extrabold px-1.5 py-0.5 rounded-full bg-slate-700 text-white uppercase tracking-wide" data-keep-color>edited</span>
                            )}
                          </div>
                          <div className="text-[10.5px] text-slate-500 dark:text-slate-400 mono truncate">{lr.key}</div>
                        </div>
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => openLegacyEdit(lr)}
                            className="w-7 h-7 grid place-items-center rounded-md hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-600 dark:text-slate-300"
                            data-testid={`ma-legacy-role-edit-${lr.key}`}
                            title="Edit modules for this role"
                          >
                            <Edit3 size={13} />
                          </button>
                          <button
                            onClick={() => deleteLegacyRole(lr)}
                            className="w-7 h-7 grid place-items-center rounded-md hover:bg-rose-50 dark:hover:bg-rose-500/15 text-rose-600 dark:text-rose-300"
                            data-testid={`ma-legacy-role-del-${lr.key}`}
                            title="Delete override — resets this role to the ERP default modules"
                          >
                            <Trash2 size={13} />
                          </button>
                        </div>
                      </div>
                      <div className="flex items-center gap-1.5 flex-wrap mb-2">
                        <Badge className="bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-300 border-amber-200 dark:border-amber-500/40 text-[10px]">
                          {modCount} module{modCount === 1 ? "" : "s"}
                        </Badge>
                        <span className="inline-flex items-center gap-1 text-[10.5px] font-bold px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200 border border-slate-200 dark:border-slate-700">
                          <Users size={11} /> {lr.user_count || 0} user{lr.user_count === 1 ? "" : "s"}
                        </span>
                      </div>
                      {lr.module_labels && lr.module_labels.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {lr.module_labels.slice(0, 12).map((label, i) => (
                            <span key={i} className="text-[10px] font-semibold px-1.5 py-0.5 rounded-md bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200 border border-slate-200 dark:border-slate-700">
                              {label}
                            </span>
                          ))}
                          {lr.module_labels.length > 12 && (
                            <span className="text-[10px] italic text-slate-500">+{lr.module_labels.length - 12} more</span>
                          )}
                        </div>
                      ) : (
                        <div className="text-[11px] italic text-slate-400">No default modules</div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <Dialog open={!!roleDialog} onOpenChange={(v) => !v && setRoleDialog(null)}>
            <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="ma-role-dialog">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <KeyRound size={18} className="text-amber-600" />
                  {roleDialog?.id ? "Edit Role" : "New Role"}
                </DialogTitle>
              </DialogHeader>
              {roleDialog && (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <label className="text-[10.5px] uppercase tracking-wider font-bold text-slate-600 dark:text-slate-300 mb-1 block">Name</label>
                      <Input value={roleDialog.name} onChange={(e) => setRoleDialog({ ...roleDialog, name: e.target.value })} placeholder="e.g. HR Manager" data-testid="ma-role-name" />
                    </div>
                    <div>
                      <label className="text-[10.5px] uppercase tracking-wider font-bold text-slate-600 dark:text-slate-300 mb-1 block">Description</label>
                      <Textarea rows={2} value={roleDialog.description} onChange={(e) => setRoleDialog({ ...roleDialog, description: e.target.value })} placeholder="What this role represents" data-testid="ma-role-desc" />
                    </div>
                  </div>
                  <ModulePermEditor
                    catalog={catalog}
                    permissions={roleDialog.permissions}
                    onChange={(next) => setRoleDialog({ ...roleDialog, permissions: next })}
                  />
                </div>
              )}
              <DialogFooter>
                <Button variant="outline" onClick={() => setRoleDialog(null)}>Cancel</Button>
                <Button onClick={saveRole} disabled={savingRole} data-testid="ma-role-save">
                  <Save size={13} className="mr-1.5" /> {roleDialog?.id ? "Save Changes" : "Create Role"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* v109.9.62 — Role Members dialog */}
          {roleMembersDialog && (
            <RoleMembersDialog
              role={roleMembersDialog.role}
              users={users}
              initialUserIds={roleMembersDialog.userIds}
              onClose={() => setRoleMembersDialog(null)}
              onSave={saveRoleMembers}
            />
          )}

          {/* v109.9.64 — Legacy role modules edit dialog */}
          <Dialog open={!!legacyEdit} onOpenChange={(o) => !o && setLegacyEdit(null)}>
            <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="ma-legacy-edit-dialog">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <KeyRound size={18} className="text-amber-600" />
                  Edit modules for {legacyEdit?.label}
                </DialogTitle>
              </DialogHeader>
              {legacyEdit && (
                <div className="space-y-3">
                  <div className="text-[11.5px] text-slate-500 dark:text-slate-400">
                    Toggle modules on/off for the <b>{legacyEdit.label}</b> legacy role. Anyone with <code className="text-[10.5px] px-1 rounded bg-slate-100 dark:bg-slate-800 mono">{legacyEdit.key}</code> as their primary role picks up this list. Reset returns the role to the ERP&apos;s hard-coded default.
                  </div>
                  <div className="text-[10.5px] text-slate-500 dark:text-slate-400 flex items-center justify-between">
                    <span>{legacyEdit.modules.length} of {legacyAllModules.length} modules selected</span>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => setLegacyEdit({ ...legacyEdit, modules: [...legacyAllModules] })}
                        className="text-[10.5px] font-semibold text-indigo-600 dark:text-indigo-300 hover:underline"
                        data-testid="ma-legacy-select-all"
                      >Select all</button>
                      <span className="text-slate-300">·</span>
                      <button
                        type="button"
                        onClick={() => setLegacyEdit({ ...legacyEdit, modules: [] })}
                        className="text-[10.5px] font-semibold text-slate-500 hover:underline"
                        data-testid="ma-legacy-clear-all"
                      >Clear</button>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 max-h-[52vh] overflow-y-auto rounded-lg border border-slate-200 dark:border-slate-700 p-2 bg-white dark:bg-slate-900">
                    {legacyAllModules.map((mk) => {
                      const label = (catalog.find((m) => m.key === mk) || {}).label || mk;
                      const on = legacyEdit.modules.includes(mk);
                      return (
                        <label
                          key={mk}
                          className={`flex items-center gap-2 px-2 py-1 rounded-md cursor-pointer text-[12px] ${on ? "bg-indigo-50 dark:bg-indigo-500/10" : "hover:bg-slate-50 dark:hover:bg-slate-800/60"}`}
                          data-testid={`ma-legacy-mod-${mk}`}
                        >
                          <input
                            type="checkbox"
                            checked={on}
                            onChange={(e) => {
                              const cur = new Set(legacyEdit.modules);
                              if (e.target.checked) cur.add(mk); else cur.delete(mk);
                              setLegacyEdit({ ...legacyEdit, modules: Array.from(cur) });
                            }}
                            className="w-3.5 h-3.5 accent-indigo-600"
                          />
                          <span className="flex-1 truncate font-semibold text-slate-800 dark:text-slate-100">{label}</span>
                          <span className="mono text-[9.5px] text-slate-400">{mk}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}
              <DialogFooter className="gap-2">
                <Button variant="outline" onClick={() => setLegacyEdit(null)}>Cancel</Button>
                <Button variant="outline" onClick={resetLegacyEdit} className="text-slate-600" data-testid="ma-legacy-reset">Reset to default</Button>
                <Button onClick={saveLegacyEdit} data-testid="ma-legacy-save">
                  <Save size={13} className="mr-1.5" /> Save Modules
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </TabsContent>

        {/* ═════════════ COMMON ═════════════ */}
        <TabsContent value="common" className="mt-4">
          <div className="pk-card p-4" data-testid="ma-common">
            <div className="text-[12px] font-bold text-slate-700 dark:text-slate-200 mb-1">Common (view-only) — Module · Tab · Sub-Tab</div>
            <div className="text-[11px] text-slate-500 dark:text-slate-400 mb-3">
              Anything you tick here is automatically <b>visible (view-only)</b> to every logged-in employee — old, current, or future — with no per-user grant required. You can mark a whole <b>Module</b>, a specific <b>Tab</b>, or a single <b>Sub-Tab</b>. Edit / Delete still require an explicit grant.
            </div>
            <CommonTreeEditor
              catalog={catalog}
              selected={commonScopes}
              fixed={fixedScopes}
              onToggle={toggleCommon}
            />
          </div>
        </TabsContent>

        {/* ═════════════ ALL MODULES & TABS (module-centric editor) ═════════════ */}
        <TabsContent value="modules" className="mt-4">
          <AllModulesTabsEditor
            catalog={catalog}
            users={users}
            matrix={matrix}
            commonScopes={commonScopes}
            onToggleCommon={toggleCommon}
            onReload={loadAll}
          />
        </TabsContent>

        {/* ═════════════ MATRIX ═════════════ */}
        <TabsContent value="matrix" className="mt-4">
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <button
              onClick={() => setMatrixView(matrixView === "user" ? "module" : "user")}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-indigo-600 text-white text-[11px] font-bold hover:bg-indigo-700 active:scale-95 transition-all"
              data-testid="ma-matrix-toggle"
            >
              <ArrowLeftRight size={12} />
              {matrixView === "user" ? "User × Module" : "Module × User"}
            </button>
            <div className="relative flex-1 min-w-[220px] max-w-xs">
              <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                value={matrixSearch}
                onChange={(e) => setMatrixSearch(e.target.value)}
                placeholder={matrixView === "user" ? "Search employees…" : "Search modules…"}
                className="pl-8 h-8 text-[12px]"
                data-testid="ma-matrix-search"
              />
            </div>
            <div className="text-[10.5px] text-slate-500 dark:text-slate-400">
              V=View, E=Edit, D=Delete · Master Admin gets everything (not shown).
            </div>
          </div>

          {matrixView === "user" ? (
            <UserByModuleMatrix
              users={matrixFilteredUsers}
              catalog={catalog}
              testid="ma-matrix-user"
            />
          ) : (
            <ModuleByUserMatrix
              matrix={matrix.modules || {}}
              catalog={catalog}
              search={matrixSearch}
              testid="ma-matrix-module"
            />
          )}
        </TabsContent>
      </Tabs>
      {loading && <div className="text-[11px] text-slate-500 dark:text-slate-400 text-center py-2">Loading…</div>}
    </div>
  );
}

// Plain function (not a hook) — safe to call after early returns.
function matrixSearchFilter(users, search) {
  const q = (search || "").toLowerCase().trim();
  if (!q) return users || [];
  return (users || []).filter(
    (u) => u.name?.toLowerCase().includes(q) || u.email?.toLowerCase().includes(q) || u.employee_code?.toLowerCase().includes(q)
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Matrix — User × Module
// ─────────────────────────────────────────────────────────────────────────
function UserByModuleMatrix({ users, catalog, testid }) {
  return (
    <div className="pk-card overflow-hidden" data-testid={testid}>
      <div className="overflow-x-auto max-w-full">
        <table className="w-full text-[11.5px] border-collapse">
          <thead>
            <tr className="border-b border-slate-200 dark:border-slate-700 bg-slate-50/70 dark:bg-slate-800/50">
              <th className="text-left px-3 py-2 font-bold sticky left-0 bg-slate-50/70 dark:bg-slate-800/50 z-10 min-w-[180px]">Employee</th>
              {catalog.map((m) => (
                <th key={m.key} className="text-center px-2 py-2 font-semibold text-[10px] uppercase tracking-wider text-slate-600 dark:text-slate-300 whitespace-nowrap">
                  <div>{m.label}</div>
                  <div className="text-[9px] font-mono text-slate-400 dark:text-slate-500">{m.key}</div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {users.length === 0 && (
              <tr><td colSpan={catalog.length + 1} className="text-center py-8 text-slate-500 dark:text-slate-400">No employees</td></tr>
            )}
            {users.map((u) => (
              <tr key={u.user_id} className="border-b border-slate-100 dark:border-slate-800 hover:bg-indigo-50/30 dark:hover:bg-indigo-500/8">
                <td className="px-3 py-2 sticky left-0 bg-white dark:bg-slate-900 z-10">
                  <div className="font-bold text-slate-900 dark:text-white truncate max-w-[220px]">{u.name}</div>
                  <div className="text-[10px] text-slate-500 dark:text-slate-400 truncate max-w-[220px]">{u.role}</div>
                </td>
                {catalog.map((m) => {
                  const p = u.modules?.[m.key];
                  return (
                    <td key={m.key} className="text-center px-2 py-2 whitespace-nowrap">
                      {!p ? (
                        <span className="text-slate-300 dark:text-slate-700">—</span>
                      ) : (
                        <PermChips p={p} />
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Matrix — Module × User
// ─────────────────────────────────────────────────────────────────────────
function ModuleByUserMatrix({ matrix, catalog, search, testid }) {
  const q = (search || "").toLowerCase().trim();
  const filteredModules = useMemo(() => {
    if (!q) return catalog;
    return catalog.filter((m) => m.key.includes(q) || m.label.toLowerCase().includes(q) || (m.group || "").toLowerCase().includes(q));
  }, [catalog, q]);
  return (
    <div className="space-y-3" data-testid={testid}>
      {filteredModules.map((m) => {
        const users = matrix[m.key] || [];
        return (
          <div key={m.key} className="pk-card p-3" data-testid={`ma-matrix-mod-${m.key}`}>
            <div className="flex items-center gap-2 mb-2">
              <div className="text-[13px] font-bold text-slate-900 dark:text-white truncate">{m.label}</div>
              <div className="text-[10px] mono text-slate-500 dark:text-slate-400">{m.group} · {m.key}</div>
              <Badge className="ml-auto bg-indigo-100 text-indigo-700 dark:bg-indigo-500/20 dark:text-indigo-300 text-[10px]">{users.length} user{users.length === 1 ? "" : "s"}</Badge>
            </div>
            {users.length === 0 ? (
              <div className="text-[11px] text-slate-500 dark:text-slate-400 italic">No user has access (besides Master Admin).</div>
            ) : (
              <div className="flex flex-wrap gap-1.5">
                {users.map((u) => (
                  <div key={u.user_id + m.key} className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700">
                    <span className="text-[11px] font-semibold text-slate-800 dark:text-slate-200">{u.name}</span>
                    <PermChips p={u} />
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function PermChips({ p }) {
  return (
    <span className="inline-flex items-center gap-0.5">
      {ACTIONS.map(({ key, label }) => (
        <span
          key={key}
          className={`inline-flex items-center justify-center w-5 h-5 rounded text-[9px] font-extrabold ${
            p[key]
              ? key === "view"
                ? "bg-sky-500/90 text-white"
                : key === "edit"
                ? "bg-amber-500/90 text-white"
                : "bg-rose-500/90 text-white"
              : "bg-slate-100 dark:bg-slate-800 text-slate-300 dark:text-slate-600"
          }`}
          title={label}
          data-keep-color
        >
          {label[0]}
        </span>
      ))}
    </span>
  );
}


// ─────────────────────────────────────────────────────────────────────────
// AllModulesTabsEditor — Module-centric grant editor.
//
// Left rail: full tree of Modules → Tabs.  Click a node to select it.
// Right pane: for the currently-selected scope (module or tab), lists every
//   employee with three toggles (View · Edit · Delete). Editing calls
//   `PUT /access/grants/:user_id` with the union of the user's existing
//   overrides and the new value for this scope.
// ─────────────────────────────────────────────────────────────────────────
function AllModulesTabsEditor({ catalog, users, matrix, commonScopes = [], onToggleCommon, onReload }) {
  const [selected, setSelected] = useState(() =>
    catalog.length ? { moduleKey: catalog[0].key, tabKey: null, subtabKey: null } : null
  );
  const [expanded, setExpanded] = useState({});
  const [q, setQ] = useState("");
  const [savingId, setSavingId] = useState("");

  const isCommon = (scope) => commonScopes.includes(scope);
  const currentScope = selected
    ? [selected.moduleKey, selected.tabKey, selected.subtabKey].filter(Boolean).join(".")
    : "";
  const scopeIsCommon = isCommon(currentScope);
  // A user inherits access via Common if either the current scope itself, its
  // parent tab, or the parent module is in the common list.
  const inheritedFromCommon = useMemo(() => {
    if (!selected) return false;
    const { moduleKey, tabKey, subtabKey } = selected;
    if (isCommon(moduleKey)) return true;
    if (tabKey && isCommon(`${moduleKey}.${tabKey}`)) return true;
    if (subtabKey && isCommon(`${moduleKey}.${tabKey}.${subtabKey}`)) return true;
    return false;
  }, [selected, commonScopes]);

  // Group catalog by group name for the left rail.
  const grouped = useMemo(() => {
    const map = new Map();
    catalog.forEach((m) => {
      const g = m.group || "Other";
      if (!map.has(g)) map.set(g, []);
      map.get(g).push(m);
    });
    return Array.from(map.entries());
  }, [catalog]);

  // Build a fast lookup of current permissions from the matrix (user × module).
  const userPermFor = (uid, moduleKey, tabKey) => {
    // Find the user row in matrix.users
    const row = (matrix.users || []).find((u) => u.user_id === uid);
    if (!row) return { view: false, edit: false, delete: false };
    const modP = row.modules?.[moduleKey];
    if (!modP) return { view: false, edit: false, delete: false };
    // The matrix stores module-level perms only. For tab-level, we defer to
    // the same module-level perms unless the user has an explicit tab override
    // (which we can't see from the matrix alone). Best-effort: reflect module
    // perms as the baseline for the tab cell.
    if (!tabKey) return modP;
    return modP;
  };

  const filteredUsers = useMemo(() => {
    const s = (q || "").toLowerCase().trim();
    if (!s) return users;
    return users.filter((u) =>
      (u.name || "").toLowerCase().includes(s) ||
      (u.email || "").toLowerCase().includes(s) ||
      (u.employee_code || "").toLowerCase().includes(s)
    );
  }, [users, q]);

  const togglePerm = async (u, moduleKey, tabKey, action, next) => {
    setSavingId(u.id);
    try {
      // Read current grant to preserve overrides for other modules/tabs.
      const { data: cur } = await api.get(`/access/grants/${u.id}`);
      const overrides = { ...(cur?.overrides || {}) };
      const mod = { ...(overrides[moduleKey] || { view: false, edit: false, delete: false }) };
      if (!tabKey) {
        mod[action] = next;
      } else {
        const tabs = { ...(mod.tabs || {}) };
        const tp = { ...(tabs[tabKey] || { view: false, edit: false, delete: false }) };
        tp[action] = next;
        tabs[tabKey] = tp;
        mod.tabs = tabs;
      }
      overrides[moduleKey] = mod;
      // v109.9.119 — Preserve the user's *multi-role* assignment. The old
      // payload passed only the legacy `role_id`, which the backend then
      // collapsed to `role_ids = [role_id]` — silently dropping every
      // secondary role. That in turn wiped the user's role-derived common
      // + module access.
      const rids = Array.isArray(cur?.role_ids) && cur.role_ids.length
        ? cur.role_ids
        : (cur?.role_id ? [cur.role_id] : []);
      await api.put(`/access/grants/${u.id}`, { role_ids: rids, overrides });
      toast.success(`Updated ${u.name}`);
      onReload();
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
    } finally { setSavingId(""); }
  };

  const selectedModule = selected ? catalog.find((m) => m.key === selected.moduleKey) : null;
  const selectedTab = selected?.tabKey && selectedModule
    ? (selectedModule.tabs || []).find((t) => t.key === selected.tabKey)
    : null;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[320px,1fr] gap-4" data-testid="ma-modules-editor">
      {/* Left rail — module/tab/sub-tab tree */}
      <div className="pk-card p-2 max-h-[70vh] overflow-y-auto">
        <div className="text-[10.5px] uppercase tracking-wider font-bold text-slate-600 dark:text-slate-300 px-2 py-1.5">All Modules &amp; Tabs</div>
        {grouped.map(([g, mods]) => (
          <div key={g} className="mb-1.5">
            <div className="px-2 py-1 text-[10px] uppercase tracking-wider font-bold text-slate-400 dark:text-slate-500">{g}</div>
            {mods.map((mod) => {
              const isSelectedMod = selected?.moduleKey === mod.key && !selected?.tabKey;
              const isOpen = !!expanded[mod.key];
              const hasTabs = (mod.tabs || []).length > 0;
              const modCommon = isCommon(mod.key);
              return (
                <div key={mod.key}>
                  <div className="flex items-center gap-1">
                    {hasTabs ? (
                      <button
                        type="button"
                        onClick={() => setExpanded({ ...expanded, [mod.key]: !isOpen })}
                        className="w-5 h-5 grid place-items-center text-slate-500 hover:text-indigo-600 dark:text-slate-400"
                        data-testid={`ma-modules-expand-${mod.key}`}
                      >
                        {isOpen ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
                      </button>
                    ) : <span className="w-5" />}
                    <button
                      type="button"
                      onClick={() => setSelected({ moduleKey: mod.key, tabKey: null, subtabKey: null })}
                      className={`flex-1 text-left px-2 py-1 text-[12px] rounded-md font-semibold truncate flex items-center gap-1.5 ${
                        isSelectedMod
                          ? "bg-indigo-100 dark:bg-indigo-500/20 text-indigo-800 dark:text-indigo-200"
                          : "hover:bg-slate-50 dark:hover:bg-slate-800/60 text-slate-700 dark:text-slate-200"
                      }`}
                      data-testid={`ma-modules-node-${mod.key}`}
                    >
                      <span className="truncate">{mod.label}</span>
                      {modCommon && (
                        <span className="ml-auto inline-flex items-center gap-0.5 text-[9px] font-extrabold px-1.5 py-0.5 rounded-full bg-emerald-600 text-white uppercase tracking-wide" data-keep-color data-testid={`ma-modules-common-tag-${mod.key}`}>
                          <Check size={9} strokeWidth={3} /> Common
                        </span>
                      )}
                    </button>
                  </div>
                  {hasTabs && isOpen && (
                    <div className="pl-6 mt-0.5 space-y-0.5">
                      {mod.tabs.map((t) => {
                        const isSelectedTab = selected?.moduleKey === mod.key && selected?.tabKey === t.key && !selected?.subtabKey;
                        const tabExpandKey = `${mod.key}::${t.key}`;
                        const tabOpen = !!expanded[tabExpandKey];
                        const hasSubs = (t.subtabs || []).length > 0;
                        const tabCommonKey = `${mod.key}.${t.key}`;
                        const tabCommon = isCommon(tabCommonKey);
                        return (
                          <div key={t.key}>
                            <div className="flex items-center gap-1">
                              {hasSubs ? (
                                <button
                                  type="button"
                                  onClick={() => setExpanded({ ...expanded, [tabExpandKey]: !tabOpen })}
                                  className="w-4 h-4 grid place-items-center text-slate-500 hover:text-indigo-600 dark:text-slate-400"
                                  data-testid={`ma-modules-expand-${mod.key}-${t.key}`}
                                >
                                  {tabOpen ? <ChevronDown size={11} /> : <ChevronRight size={11} />}
                                </button>
                              ) : <span className="w-4" />}
                              <button
                                type="button"
                                onClick={() => setSelected({ moduleKey: mod.key, tabKey: t.key, subtabKey: null })}
                                className={`flex-1 text-left px-2 py-1 text-[11.5px] rounded-md truncate flex items-center gap-1.5 ${
                                  isSelectedTab
                                    ? "bg-indigo-100 dark:bg-indigo-500/20 text-indigo-800 dark:text-indigo-200 font-bold"
                                    : "hover:bg-slate-50 dark:hover:bg-slate-800/60 text-slate-600 dark:text-slate-300"
                                }`}
                                data-testid={`ma-modules-node-${mod.key}-${t.key}`}
                              >
                                <span className="text-slate-400 mr-1">└</span>
                                <span className="truncate">{t.label}</span>
                                {tabCommon && (
                                  <span className="ml-auto inline-flex items-center gap-0.5 text-[9px] font-extrabold px-1.5 py-0.5 rounded-full bg-emerald-600 text-white uppercase tracking-wide" data-keep-color data-testid={`ma-modules-common-tag-${mod.key}-${t.key}`}>
                                    <Check size={9} strokeWidth={3} /> Common
                                  </span>
                                )}
                              </button>
                            </div>
                            {hasSubs && tabOpen && (
                              <div className="pl-6 mt-0.5 space-y-0.5">
                                {t.subtabs.map((st) => {
                                  const isSelSt = selected?.moduleKey === mod.key && selected?.tabKey === t.key && selected?.subtabKey === st.key;
                                  const stCommonKey = `${mod.key}.${t.key}.${st.key}`;
                                  const stCommon = isCommon(stCommonKey);
                                  return (
                                    <button
                                      key={st.key}
                                      type="button"
                                      onClick={() => setSelected({ moduleKey: mod.key, tabKey: t.key, subtabKey: st.key })}
                                      className={`w-full text-left px-2 py-1 text-[11px] rounded-md truncate flex items-center gap-1.5 ${
                                        isSelSt
                                          ? "bg-indigo-100 dark:bg-indigo-500/20 text-indigo-800 dark:text-indigo-200 font-bold"
                                          : "hover:bg-slate-50 dark:hover:bg-slate-800/60 text-slate-500 dark:text-slate-400"
                                      }`}
                                      data-testid={`ma-modules-node-${mod.key}-${t.key}-${st.key}`}
                                    >
                                      <span className="text-slate-300 mr-1">┗</span>
                                      <span className="truncate">{st.label}</span>
                                      {stCommon && (
                                        <span className="ml-auto inline-flex items-center gap-0.5 text-[9px] font-extrabold px-1.5 py-0.5 rounded-full bg-emerald-600 text-white uppercase tracking-wide" data-keep-color data-testid={`ma-modules-common-tag-${mod.key}-${t.key}-${st.key}`}>
                                          <Check size={9} strokeWidth={3} /> Common
                                        </span>
                                      )}
                                    </button>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ))}
      </div>

      {/* Right pane — user access editor for the selected scope */}
      <div className="pk-card p-3">
        {!selectedModule ? (
          <div className="text-center py-16 text-[12px] text-slate-500">Select a module or tab from the left to manage access.</div>
        ) : (
          <>
            <div className="flex flex-wrap items-center gap-2 mb-3">
              <div className="min-w-0">
                <div className="text-[10.5px] uppercase tracking-wider font-bold text-slate-500 dark:text-slate-400">
                  {selected.subtabKey ? "Sub-Tab" : selectedTab ? "Tab" : "Module"}
                </div>
                <div className="text-[15px] font-extrabold text-slate-900 dark:text-white truncate flex items-center gap-2">
                  <span>
                    {selectedModule.label}
                    {selectedTab && <> <span className="text-slate-400">›</span> {selectedTab.label}</>}
                    {selected.subtabKey && selectedTab && (() => {
                      const st = (selectedTab.subtabs || []).find((x) => x.key === selected.subtabKey);
                      return st ? <> <span className="text-slate-400">›</span> {st.label}</> : null;
                    })()}
                  </span>
                  {scopeIsCommon && (
                    <span className="inline-flex items-center gap-1 text-[10px] font-extrabold uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-600 text-white" data-keep-color data-testid="ma-modules-scope-common-tag">
                      <Check size={10} strokeWidth={3} /> Common
                    </span>
                  )}
                </div>
                <div className="text-[10.5px] mono text-slate-500 dark:text-slate-400">
                  {currentScope}
                </div>
              </div>
              {onToggleCommon && (
                <button
                  type="button"
                  onClick={() => onToggleCommon(currentScope)}
                  className={`ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11.5px] font-bold transition-all ${
                    scopeIsCommon
                      ? "bg-emerald-100 dark:bg-emerald-500/20 text-emerald-800 dark:text-emerald-200 border border-emerald-300 dark:border-emerald-500/50 hover:bg-emerald-200 dark:hover:bg-emerald-500/30"
                      : "bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200 border border-slate-300 dark:border-slate-600 hover:bg-emerald-50 dark:hover:bg-emerald-500/10 hover:border-emerald-300 dark:hover:border-emerald-500/40"
                  }`}
                  data-keep-color={scopeIsCommon ? "" : undefined}
                  data-testid="ma-modules-toggle-common"
                >
                  <Check size={12} strokeWidth={3} />
                  {scopeIsCommon ? "Remove from Common" : "Mark as Common"}
                </button>
              )}
              <div className="relative w-full sm:w-72">
                <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Search employees…"
                  className="pl-8 h-8 text-[12px]"
                  data-testid="ma-modules-user-search"
                />
              </div>
            </div>

            <div className="overflow-x-auto max-h-[62vh] overflow-y-auto rounded-lg border border-slate-200 dark:border-slate-700">
              <table className="w-full text-[12px]">
                <thead className="bg-slate-50 dark:bg-slate-800 sticky top-0 z-10">
                  <tr>
                    <th className="text-left px-3 py-2 font-bold text-[10.5px] uppercase tracking-wider text-slate-600 dark:text-slate-300">Employee</th>
                    <th className="text-left px-3 py-2 font-bold text-[10.5px] uppercase tracking-wider text-slate-600 dark:text-slate-300">Role</th>
                    <th className="text-center px-3 py-2 font-bold text-[10.5px] uppercase tracking-wider text-sky-700 dark:text-sky-300">View</th>
                    <th className="text-center px-3 py-2 font-bold text-[10.5px] uppercase tracking-wider text-amber-700 dark:text-amber-300">Edit</th>
                    <th className="text-center px-3 py-2 font-bold text-[10.5px] uppercase tracking-wider text-rose-700 dark:text-rose-300">Delete</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.length === 0 && (
                    <tr><td colSpan={5} className="text-center py-6 text-slate-400">No employees</td></tr>
                  )}
                  {filteredUsers.map((u) => {
                    const p = userPermFor(u.id, selectedModule.key, selectedTab?.key);
                    return (
                      <tr key={u.id} className="border-t border-slate-100 dark:border-slate-800" data-testid={`ma-modules-row-${u.id}`}>
                        <td className="px-3 py-1.5">
                          <div className="font-semibold text-slate-900 dark:text-white truncate flex items-center gap-1.5">
                            <span className="truncate">{u.name}</span>
                            {inheritedFromCommon && (
                              <span className="inline-flex items-center gap-0.5 text-[8.5px] font-extrabold px-1.5 py-0.5 rounded-full bg-emerald-600 text-white uppercase tracking-wide" data-keep-color data-testid={`ma-modules-user-common-tag-${u.id}`}>
                                <Check size={8} strokeWidth={3} /> Common
                              </span>
                            )}
                          </div>
                          <div className="text-[10px] text-slate-500 dark:text-slate-400 truncate">{u.email}</div>
                        </td>
                        <td className="px-3 py-1.5 text-[11px] text-slate-600 dark:text-slate-300">{u.role}</td>
                        {["view", "edit", "delete"].map((action) => (
                          <td key={action} className="px-3 py-1.5 text-center">
                            <button
                              type="button"
                              disabled={savingId === u.id}
                              onClick={() => togglePerm(u, selectedModule.key, selectedTab?.key || null, action, !p[action])}
                              className={`inline-flex items-center justify-center w-8 h-6 rounded font-extrabold text-[10.5px] transition-all ${
                                p[action]
                                  ? action === "view"
                                    ? "bg-sky-500 text-white shadow-sm"
                                    : action === "edit"
                                    ? "bg-amber-500 text-white shadow-sm"
                                    : "bg-rose-500 text-white shadow-sm"
                                  : "bg-slate-100 dark:bg-slate-800 text-slate-400 dark:text-slate-500 hover:bg-slate-200 dark:hover:bg-slate-700"
                              } disabled:opacity-50`}
                              data-testid={`ma-modules-toggle-${u.id}-${action}`}
                              title={p[action] ? "Click to revoke" : "Click to grant"}
                              data-keep-color
                            >
                              {p[action] ? <Check size={12} /> : <X size={12} />}
                            </button>
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="mt-2 text-[10.5px] text-slate-500 dark:text-slate-400">
              Tip: cells reflect module-level access. For fine-grained tab overrides, edits here save into the user&apos;s <b>Overrides</b> — you can also view the full grant per user in the <b>Grants</b> tab.
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// CommonTreeEditor — module → tab → sub-tab checkbox tree for Common access.
//
// Each node can be individually toggled. Ticking a Module implicitly makes
// EVERY tab / sub-tab under it view-only to all users; ticking a specific
// Tab or Sub-Tab narrows the common surface to just that node. Scopes are
// stored server-side as dot-notation strings ("hr", "hr.employees",
// "hr.employees.active"), which map 1:1 to the checkbox each user ticks.
// ─────────────────────────────────────────────────────────────────────────
function CommonTreeEditor({ catalog, selected, fixed = [], onToggle }) {
  const [expanded, setExpanded] = useState({});
  const fixedSet = useMemo(() => new Set(fixed || []), [fixed]);
  const isFixed = (scope) => fixedSet.has(scope);
  const isOn = (scope) => selected.includes(scope) || fixedSet.has(scope);

  const allExpanded = useMemo(
    () => catalog.filter((m) => (m.tabs || []).length > 0).every((m) => expanded[m.key]),
    [expanded, catalog],
  );
  const expandAll = () => {
    const next = {};
    catalog.forEach((m) => { if ((m.tabs || []).length > 0) next[m.key] = true; });
    setExpanded(next);
  };
  const collapseAll = () => setExpanded({});

  // Group modules by their `group` for a scannable layout.
  const grouped = useMemo(() => {
    const map = new Map();
    catalog.forEach((m) => {
      const g = m.group || "Other";
      if (!map.has(g)) map.set(g, []);
      map.get(g).push(m);
    });
    return Array.from(map.entries());
  }, [catalog]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="text-[11px] text-slate-500 dark:text-slate-400">
          {selected.length} scope{selected.length === 1 ? "" : "s"} marked as Common
        </div>
        <button
          type="button"
          onClick={allExpanded ? collapseAll : expandAll}
          className="inline-flex items-center gap-1 text-[11.5px] font-semibold px-3 py-1.5 rounded-md border border-emerald-300 dark:border-emerald-500/50 text-emerald-700 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-500/10 hover:bg-emerald-100 dark:hover:bg-emerald-500/20"
          data-testid="ma-common-expand-all"
        >
          {allExpanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
          {allExpanded ? "Collapse all" : "Expand all"}
        </button>
      </div>
      {grouped.map(([g, mods]) => (
        <div key={g} className="border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden">
          <div className="px-3 py-2 bg-slate-50 dark:bg-slate-800 text-[11px] uppercase tracking-wider font-bold text-slate-600 dark:text-slate-300 border-b border-slate-200 dark:border-slate-700">
            {g}
          </div>
          <div className="divide-y divide-slate-100 dark:divide-slate-800">
            {mods.map((mod) => {
              const modOn = isOn(mod.key);
              const isOpen = !!expanded[mod.key];
              const hasTabs = (mod.tabs || []).length > 0;
              return (
                <div key={mod.key}>
                  <div className={`px-3 py-2 flex items-center gap-2 ${modOn ? "bg-emerald-50 dark:bg-emerald-500/10" : ""}`}>
                    {hasTabs ? (
                      <button
                        type="button"
                        onClick={() => setExpanded({ ...expanded, [mod.key]: !isOpen })}
                        className="w-5 h-5 grid place-items-center text-slate-500 hover:text-emerald-600 dark:text-slate-400"
                        data-testid={`ma-common-expand-${mod.key}`}
                      >
                        {isOpen ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
                      </button>
                    ) : <span className="w-5" />}
                    <label className="flex-1 flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={modOn}
                        onChange={() => onToggle(mod.key)}
                        disabled={isFixed(mod.key)}
                        className="w-4 h-4 accent-emerald-600"
                        data-testid={`ma-common-${mod.key}`}
                      />
                      <span className="text-[12.5px] font-bold text-slate-900 dark:text-white">{mod.label}</span>
                      <span className="text-[10px] text-slate-500 dark:text-slate-400 mono">{mod.key}</span>
                      {isFixed(mod.key) && (
                        <span className="inline-flex items-center gap-1 text-[9.5px] font-bold px-1.5 py-0.5 rounded-full bg-indigo-600 text-white" data-keep-color>LOCKED</span>
                      )}
                    </label>
                    {modOn && (
                      <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-600 text-white" data-keep-color>
                        <Check size={10} /> Common
                      </span>
                    )}
                  </div>

                  {hasTabs && isOpen && (
                    <div className="pl-10 pr-3 pb-3 pt-1 bg-slate-50/60 dark:bg-slate-800/30 space-y-1.5">
                      {mod.tabs.map((t) => {
                        const tabScope = `${mod.key}.${t.key}`;
                        const tabOn = isOn(tabScope);
                        const impliedByMod = modOn;
                        const hasSubs = (t.subtabs || []).length > 0;
                        return (
                          <div key={t.key}>
                            <label className={`flex items-center gap-2 cursor-pointer py-0.5 ${impliedByMod ? "opacity-60" : ""}`}>
                              <input
                                type="checkbox"
                                checked={tabOn || impliedByMod}
                                onChange={() => !impliedByMod && !isFixed(tabScope) && onToggle(tabScope)}
                                disabled={impliedByMod || isFixed(tabScope)}
                                className="w-3.5 h-3.5 accent-emerald-600"
                                data-testid={`ma-common-${mod.key}-${t.key}`}
                              />
                              <span className="text-[11.5px] font-semibold text-slate-800 dark:text-slate-100">{t.label}</span>
                              <span className="text-[10px] text-slate-500 dark:text-slate-400 mono">{t.key}</span>
                              {isFixed(tabScope) && (
                                <span className="inline-flex items-center gap-1 text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-indigo-600 text-white" data-keep-color>LOCKED</span>
                              )}
                              {impliedByMod && (
                                <span className="text-[9.5px] font-bold uppercase text-emerald-700 dark:text-emerald-300">Included via module</span>
                              )}
                              {tabOn && !impliedByMod && !isFixed(tabScope) && (
                                <span className="ml-auto inline-flex items-center gap-1 text-[9.5px] font-bold px-1.5 py-0.5 rounded-full bg-emerald-500 text-white" data-keep-color>
                                  Tab-common
                                </span>
                              )}
                            </label>
                            {hasSubs && (
                              <div className="pl-6 mt-0.5 space-y-0.5">
                                {t.subtabs.map((st) => {
                                  const stScope = `${mod.key}.${t.key}.${st.key}`;
                                  const stOn = isOn(stScope);
                                  const impliedByTab = tabOn || impliedByMod;
                                  return (
                                    <label
                                      key={st.key}
                                      className={`flex items-center gap-2 cursor-pointer py-0.5 ${impliedByTab ? "opacity-60" : ""}`}
                                    >
                                      <input
                                        type="checkbox"
                                        checked={stOn || impliedByTab}
                                        onChange={() => !impliedByTab && !isFixed(stScope) && onToggle(stScope)}
                                        disabled={impliedByTab || isFixed(stScope)}
                                        className="w-3 h-3 accent-emerald-600"
                                        data-testid={`ma-common-${mod.key}-${t.key}-${st.key}`}
                                      />
                                      <span className="text-slate-400 text-[10px] mr-1">└</span>
                                      <span className="text-[11px] text-slate-700 dark:text-slate-200">{st.label}</span>
                                      <span className="text-[9.5px] text-slate-400 dark:text-slate-500 mono">{st.key}</span>
                                      {isFixed(stScope) && (
                                        <span className="inline-flex items-center gap-1 text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-indigo-600 text-white" data-keep-color>LOCKED</span>
                                      )}
                                      {impliedByTab && (
                                        <span className="text-[9px] font-bold uppercase text-emerald-700 dark:text-emerald-300">
                                          {impliedByMod ? "Via module" : "Via tab"}
                                        </span>
                                      )}
                                      {stOn && !impliedByTab && (
                                        <span className="ml-auto inline-flex items-center gap-1 text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-emerald-500 text-white" data-keep-color>
                                          Sub-common
                                        </span>
                                      )}
                                    </label>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}



// ─────────────────────────────────────────────────────────────────────────
// v109.9.62 — RoleMembersDialog
// Master Admin can pick multiple employees for a role in one shot. Any user
// already assigned who's unticked gets the role stripped from their grant
// server-side (their other roles + overrides untouched).
// ─────────────────────────────────────────────────────────────────────────
function RoleMembersDialog({ role, users, initialUserIds, onClose, onSave }) {
  const [picked, setPicked] = useState(() => new Set(initialUserIds || []));
  const [q, setQ] = useState("");
  const filtered = useMemo(() => {
    const s = (q || "").toLowerCase().trim();
    if (!s) return users;
    return users.filter((u) =>
      (u.name || "").toLowerCase().includes(s) ||
      (u.email || "").toLowerCase().includes(s) ||
      (u.employee_code || "").toLowerCase().includes(s) ||
      (u.role || "").toLowerCase().includes(s)
    );
  }, [users, q]);
  const toggle = (uid) => {
    const s = new Set(picked);
    if (s.has(uid)) s.delete(uid); else s.add(uid);
    setPicked(s);
  };
  const allVisiblePicked = filtered.length > 0 && filtered.every((u) => picked.has(u.id));
  const toggleAllVisible = () => {
    const s = new Set(picked);
    if (allVisiblePicked) filtered.forEach((u) => s.delete(u.id));
    else filtered.forEach((u) => s.add(u.id));
    setPicked(s);
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="ma-role-members-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users size={18} className="text-emerald-600" />
            {role.name} · Assigned Users
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="text-[11.5px] text-slate-500 dark:text-slate-400">
            Pick every employee who should inherit this role&apos;s permissions. Users you untick will have <b>{role.name}</b> removed from their grants (their other roles and overrides stay untouched).
          </div>
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search employees…" className="pl-8 h-9 text-[12px]" data-testid="ma-role-members-search" />
            </div>
            <Button size="sm" variant="outline" onClick={toggleAllVisible} data-testid="ma-role-members-toggle-all">
              {allVisiblePicked ? "Untick visible" : "Tick visible"}
            </Button>
          </div>
          <div className="text-[11px] text-slate-500 dark:text-slate-400">
            {picked.size} selected · {filtered.length} shown of {users.length} employees
          </div>
          <div className="rounded-lg border border-slate-200 dark:border-slate-700 max-h-[52vh] overflow-y-auto divide-y divide-slate-100 dark:divide-slate-800">
            {filtered.length === 0 && (
              <div className="p-6 text-center text-slate-400 dark:text-slate-500 text-sm">No employees match.</div>
            )}
            {filtered.map((u) => {
              const on = picked.has(u.id);
              return (
                <label key={u.id} className={`flex items-center gap-2 px-3 py-2 cursor-pointer ${on ? "bg-emerald-50/60 dark:bg-emerald-500/10" : "hover:bg-slate-50 dark:hover:bg-slate-800/60"}`} data-testid={`ma-role-members-row-${u.id}`}>
                  <input type="checkbox" checked={on} onChange={() => toggle(u.id)} className="w-4 h-4 accent-emerald-600" />
                  <div className="flex-1 min-w-0">
                    <div className="text-[13px] font-semibold text-slate-900 dark:text-white truncate">{u.name}</div>
                    <div className="text-[10.5px] text-slate-500 dark:text-slate-400 mono truncate">{u.email} · {u.role || "—"}</div>
                  </div>
                  {on && (
                    <Badge className="bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300 border-emerald-200 dark:border-emerald-500/40 text-[10px]">Assigned</Badge>
                  )}
                </label>
              );
            })}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => onSave(Array.from(picked))} data-testid="ma-role-members-save">
            <Save size={13} className="mr-1.5" /> Save Members ({picked.size})
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
